import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { SessionService, PortalSettings, User } from '@edl-development/edl-common';
import { StateService } from '@uirouter/angular';
import { RequestHelpersService } from 'src/app/services/request-helpers/request-helpers.service';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { DateHelpers } from '@edl-development/edl-common';
import { RequestUserStatus } from 'src/app/model/enum/request-user-status.enum';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { CplUserRequestSummary } from 'src/app/model/request/cpl-user-request-summary.model';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { StatusConfigKey } from 'src/app/status-aggregation-indicator/enums/status-config-key.enum';
import { STATUS_CONFIG_KEY_CONFIG, STATUS_CONFIG_KEY_TO_STATUSES } from 'src/app/status-aggregation-indicator/constants/status-config.constants';

const UNLIMITED = 'Unlimited';

@Component({
    selector: 'edl-requests-tab-list',
    templateUrl: './requests-tab-list.component.html',
    styleUrls: ['./requests-tab-list.component.scss']
})
export class RequestsTabListComponent implements OnChanges {
    @Input() requests: CplUserRequestSummary[];
    @Input() isUser: boolean = false;
    @Output() onViewSubmissions = new EventEmitter<CplUserRequestSummary>();

    activeStatusColumns: StatusConfigKey[] = [];

    portalSettings: PortalSettings;
    user: User;
    DateHelpers = DateHelpers;
    RequestUserStatus = RequestUserStatus;
    TimeTrackingType = TimeTrackingTypes;

    canViewRequestManagement: boolean = false;

    constructor(
        private sessionService: SessionService,
        private stateService: StateService,
        public requestHelpersService: RequestHelpersService,
        public a48HelpersService: A48HelpersService
    ) {
        this.portalSettings = this.sessionService.PortalSettings;
        this.user = this.sessionService.user;

        this.canViewRequestManagement =
            AccessRoleHelper.isAdmin(this.sessionService.user.AccessRoleID)
            || AccessRoleHelper.isSupervisor(this.sessionService.user.AccessRoleID)
            || AccessRoleHelper.isEDLAdmin(this.sessionService.user.AccessRoleID);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.requests && this.requests) {
            this.determineActiveStatusColumns();
        }
    }

    private determineActiveStatusColumns() {
        // Analyze all requests to find which status types exist in the dataset
        const activeStatusKeys = new Set<StatusConfigKey>();

        for (const request of this.requests) {
            if (request.StatusCounts) {
                // Check each status config key to see if any of its statuses have counts
                for (const config of STATUS_CONFIG_KEY_CONFIG) {
                    // Skip if already found active
                    if (activeStatusKeys.has(config.key)) {
                        continue;
                    }

                    const hasCount = STATUS_CONFIG_KEY_TO_STATUSES[config.key].some(
                        status => (request.StatusCounts[status] ?? 0) > 0
                    );
                    if (hasCount) {
                        activeStatusKeys.add(config.key);
                    }
                }

                // Early exit if all possible status keys are active
                if (activeStatusKeys.size === STATUS_CONFIG_KEY_CONFIG.length) {
                    break;
                }
            }
        }

        // Maintain the canonical order from STATUS_CONFIG_KEY_CONFIG
        this.activeStatusColumns = STATUS_CONFIG_KEY_CONFIG
            .filter(config => activeStatusKeys.has(config.key))
            .map(config => config.key);
    }

    goToRequest(requestId: number) {
        this.stateService.go('authenticated.requests.sm.request', { requestId: requestId });
    }

    getUserRequestLimit(request: CplUserRequestSummary) : string {
        if (!request.RequestItem.UserRequestLimit) {
            return UNLIMITED;
        }
        
        return `${request.RequestItem.UserRequestLimitCount ?? 0}`;   
    }

    viewSubmissions(request: CplUserRequestSummary) {
        this.onViewSubmissions.emit(request);
    }
}